import React, { useState, useRef, useEffect, useCallback } from "react";
import { connect } from 'react-redux';
import { Formik } from 'formik';

import { Button, SVGIcon, Label } from '../../../../components/custom-essentials';
import { BrowserTabTitle, LoadingOverlay, TooltipWrapper } from '../../../../components/display';
import { Modal } from '../../../../components/custom-essentials/Modal';
import { FormikControl, DateRangeSelector, SelectSingle, Switch, checkResponses } from '../../../../components/form';
import { formatDateApi, formatDate, exportToCSV, getDataArray } from '../../../../components/functions';
import { Table } from '../../../../components/table';
import { Socket } from '../../../../components/ws';
import ContractModal from './ContractModal';
import MathPlexContractModal from '../../../../components/modal/contracts/MathPlexContractModal';
import {
    fetchContractsDaterangeCenter,
    fetchContractsIds,
    fetchRpCentersAll,
    fetchStudentsAll,
    createContract,
    updateContract,
    deleteContract,
    fetchPaymentsDaterangeCenter,
    fetchPaymentsIds,
    fetchStudentsEssential,
    fetchContractsStudentNames,
    createPayment,
    updatePayment,
    deletePayment
} from '../../../../actions';
import MathPlexPaymentsModal from '../../../../components/modal/payments/MathPlexPaymentsModal';
import ReadingPlexPaymentsModal from '../../../../components/modal/payments/ReadingPlexPaymentsModal';
import PaymentsTable from '../contracts-payments/payments/PaymentsTable';

// Portal detection
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isReadingPlex = hostname.includes('readingplex');

// Calculate monthly breakdown for a contract using FIFO (First In First Out) payment allocation
// This ensures payments are applied to the oldest outstanding balance first, regardless of when payment was made
function calculateMonthlyBreakdown(contract, payments) {
    if (!contract || !contract.start_date) return [];
    
    const startDate = new Date(contract.start_date);
    if (isNaN(startDate.getTime())) return [];
    
    const now = new Date();
    const endDate = contract.end_date ? new Date(contract.end_date) : null;
    const latestDate = endDate && endDate < now ? endDate : now;
    
    const initialPayment = parseFloat(contract.initial_payment) || 0;
    const paymentPerCycle = parseFloat(contract.payment_per_cycle) || 0;
    
    // Step 1: Build monthly dues structure chronologically
    const monthlyDues = [];
    let currentDate = new Date(startDate);
    currentDate.setDate(1); // Start of month
    
    while (currentDate <= latestDate) {
        const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
        const monthLabel = currentDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
        
        // Calculate amount due for this month
        let monthDue = 0;
        if (currentDate.getTime() === new Date(startDate.getFullYear(), startDate.getMonth(), 1).getTime()) {
            // First month includes initial payment
            monthDue = initialPayment + paymentPerCycle;
        } else {
            monthDue = paymentPerCycle;
        }
        
        monthlyDues.push({
            month: monthKey,
            monthLabel: monthLabel,
            due: monthDue,
            paid: 0, // Will be calculated using FIFO
            balance: 0, // Will be calculated
            runningBalance: 0 // Track cumulative balance
        });
        
        // Move to next month
        currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    // Step 2: Sort payments chronologically (oldest first for FIFO)
    const sortedPayments = payments
        .filter(p => p && p.date)
        .map(p => ({
            ...p,
            date: new Date(p.date),
            amount: parseFloat(p.amount) || 0
        }))
        .filter(p => !isNaN(p.date.getTime()) && p.amount > 0)
        .sort((a, b) => a.date - b.date); // Oldest first
    
    // Step 3: Apply payments using FIFO - allocate to oldest outstanding balance first
    let paymentIndex = 0;
    let remainingPayment = sortedPayments.length > 0 ? sortedPayments[0].amount : 0;
    
    for (let i = 0; i < monthlyDues.length; i++) {
        const month = monthlyDues[i];
        
        // Calculate running balance (cumulative due minus cumulative paid)
        const previousBalance = i > 0 ? monthlyDues[i - 1].runningBalance : 0;
        month.runningBalance = previousBalance + month.due;
        
        // Apply remaining payment to this month's balance
        while (remainingPayment > 0 && month.runningBalance > 0) {
            const amountToApply = Math.min(remainingPayment, month.runningBalance);
            month.paid += amountToApply;
            month.runningBalance -= amountToApply;
            remainingPayment -= amountToApply;
            
            // Move to next payment if current one is exhausted
            if (remainingPayment <= 0 && paymentIndex < sortedPayments.length - 1) {
                paymentIndex++;
                remainingPayment = sortedPayments[paymentIndex].amount;
            }
        }
        
        // Calculate this month's balance (paid - due)
        month.balance = month.paid - month.due;
    }
    
    return monthlyDues;
}

// ReadingPlex-specific balance calculation (fixed to calculate based on months since contract start)
function calculateRpContractBalances(contracts, payments) {
    if (!contracts || !Array.isArray(contracts)) return [];
    if (!payments || !Array.isArray(payments)) payments = [];

    // Map payments by contract ID (handle both string and number IDs)
    const contractPaymentMap = {};
    payments.forEach(p => {
        if (!p || !p.contract) return; // Skip invalid payments
        const contractId = parseInt(p.contract);
        if (isNaN(contractId)) {
            console.warn('[CONTRACTS] Invalid contract ID in payment:', p);
            return;
        }
        if (!contractPaymentMap[contractId]) contractPaymentMap[contractId] = [];
        contractPaymentMap[contractId].push(p);
    });

    const now = new Date();
    
    return contracts.map(c => {
        if (!c || !c.id) {
            console.warn('[CONTRACTS] Invalid contract:', c);
            return { ...c, balanceOwed: 0, balancePaid: 0, monthlyBreakdown: [] };
        }

        const contractId = parseInt(c.id);
        const initialPayment = parseFloat(c.initial_payment) || 0;
        const paymentPerCycle = parseFloat(c.payment_per_cycle) || 0;
        
        // Calculate balance owed based on months since contract start (not just current cycle)
        let balanceOwed = initialPayment;
        
        if (c.start_date) {
            try {
                const startDate = new Date(c.start_date);
                if (!isNaN(startDate.getTime())) {
                    let latestDate = now;
                    if (c.end_date) {
                        const endDate = new Date(c.end_date);
                        if (!isNaN(endDate.getTime())) {
                            latestDate = now > endDate ? endDate : now;
                        }
                    }
                    
                    // Calculate months since start
                    let monthsDiff = 
                        (latestDate.getFullYear() - startDate.getFullYear()) * 12 +
                        (latestDate.getMonth() - startDate.getMonth());
                    // If we're past the start day of the month, count this month
                    if (latestDate.getDate() >= startDate.getDate()) {
                        monthsDiff += 1;
                    }
                    
                    // Add payment per cycle for each month (excluding the first month which already includes initial payment)
                    if (monthsDiff > 0) {
                        balanceOwed += monthsDiff * paymentPerCycle;
                    }
                }
            } catch (error) {
                console.warn('[CONTRACTS] Error calculating balance owed:', error);
                // Fallback to old calculation if date parsing fails
                const currentCycle = parseInt(c.current_cycle) || 0;
                balanceOwed = initialPayment + (currentCycle * paymentPerCycle);
            }
        } else {
            // Fallback to old calculation if no start date
            const currentCycle = parseInt(c.current_cycle) || 0;
            balanceOwed = initialPayment + (currentCycle * paymentPerCycle);
        }

        // Calculate balance paid: sum of all payments for this contract
        const contractPayments = contractPaymentMap[contractId] || [];
        const balancePaid = contractPayments.reduce((sum, p) => {
            if (!p || p.amount === null || p.amount === undefined) return sum;
            const amount = parseFloat(p.amount);
            if (isNaN(amount)) {
                console.warn('[CONTRACTS] Invalid payment amount:', p);
                return sum;
            }
            return sum + amount;
        }, 0);
        
        // Calculate monthly breakdown for hover tooltip
        const monthlyBreakdown = calculateMonthlyBreakdown(c, contractPayments);

        c.balanceOwed = balanceOwed;
        c.balancePaid = balancePaid;
        c.monthlyBreakdown = monthlyBreakdown;
        c.relevantPayments = contractPayments.sort((a, b) => {
            const aDate = new Date(a.date);
            const bDate = new Date(b.date);
            return bDate - aDate; // Most recent first
        });
        return c;
    });
}

const pageTitle = 'Contracts and Payments';

const start = new Date();
start.setMonth(start.getMonth() - 12); // Show last 12 months by default
const startApi = formatDateApi(start);
const endApi = formatDateApi(new Date());

function Contracts(props) {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });
    const formRef = useRef();

    const [hasLoaded, setHasLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(false);
    const [contracts, setContracts] = useState([]);
    const [filteredContracts, setFilteredContracts] = useState([]);
    const [centers, setCenters] = useState([]);
    const [students, setStudents] = useState([]);
    const [activeTab, setActiveTab] = useState('contracts');
    
    // Payments state
    const [payments, setPayments] = useState([]);
    const [filteredPayments, setFilteredPayments] = useState([]);
    const [paymentSum, setPaymentSum] = useState(0);
    const paymentFormRef = useRef();
    const [paymentDrsValid, setPaymentDrsValid] = useState(true);
    
    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedContract, setSelectedContract] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentModalMode, setPaymentModalMode] = useState('create');
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
    const [selectedContractForPaymentDetails, setSelectedContractForPaymentDetails] = useState(null);

    const { fetchContractsDaterangeCenter, fetchRpCentersAll, fetchStudentsAll,
        createContract, updateContract, deleteContract,
        fetchPaymentsDaterangeCenter, fetchStudentsEssential,
        createPayment, updatePayment, deletePayment } = props;

    const handleOpenModal = React.useCallback((mode, contract = null) => {
        setModalMode(mode);
        setSelectedContract(contract);
        setShowModal(true);
    }, []);

    const handleCloseModal = (shouldRefresh = false) => {
        setShowModal(false);
        setSelectedContract(null);
        if (shouldRefresh) {
            // Refresh immediately - backend transaction is committed by the time we get here
            // Also force a re-fetch of the updated contract if we were editing
            refreshData();
        }
    };

    const filterContracts = useCallback((unfilteredContracts, filterQuery, filters = {}) => {
        let filtered = [...unfilteredContracts];
        
        const { activeContractsOnly, activeStudentsOnly, showExtra, hideMatching, monthlyOnly } = filters;
        
        // Apply filter toggles per Guide Page 10
        if (activeContractsOnly) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            filtered = filtered.filter(c => {
                if (!c.end_date || c.end_date === '0000-00-00' || c.end_date === null) return true; // Never expires
                const endDate = new Date(c.end_date);
                return endDate >= today;
            });
        }
        
        if (activeStudentsOnly) {
            filtered = filtered.filter(c => parseInt(c.studentActive) === 1);
        }
        
        if (!showExtra) {
            // Hide extra columns or data - placeholder for now
        }
        
        if (hideMatching) {
            // Hide matching entries - placeholder for now
        }
        
        if (monthlyOnly) {
            filtered = filtered.filter(c => c.payment_frequency === 'monthly');
        }
        
        // Apply search query filter
        if (filterQuery && filterQuery.trim() !== '') {
        const query = filterQuery.toLowerCase().trim();
            filtered = filtered.filter(c => {
            return (c.studentName && c.studentName.toLowerCase().includes(query)) ||
                    (c.type && c.type.toLowerCase().includes(query)) ||
                    (c.notes && c.notes.toLowerCase().includes(query));
        });
        }
        
        setFilteredContracts(filtered);
    }, []);

    const filterPayments = useCallback((unfilteredPayments, filterType, filterQuery) => {
        if (!unfilteredPayments || !Array.isArray(unfilteredPayments)) {
            setFilteredPayments([]);
            setPaymentSum(0);
            if(mounted.current) setHasLoaded(true);
            return;
        }
        
        filterQuery = (filterQuery || '').replace(/ /g, '').toLowerCase();
        const checkStudentNames = (p) => (p.studentName || '').replace(/ /g, '').toLowerCase().includes(filterQuery);
        const checkNotes = (p) => (p.notes || '').toLowerCase().includes(filterQuery);

        const filterTypeValue = filterType?.value || 'all';
        let newPayments = unfilteredPayments.filter(p => {
            if (!filterQuery) {
                // If no filter query, show all payments
                return true;
            }
            switch(filterTypeValue){
                case 'all':
                    return checkStudentNames(p) || checkNotes(p);
                case 'studentName':
                    return checkStudentNames(p);
                case 'notes':
                    return checkNotes(p);
                default:
                    return true;
            }
        });

        let newPaymentSum = 0;
        newPayments.forEach(p => newPaymentSum += parseFloat(p.amount) || 0);

        if(mounted.current){
            setFilteredPayments(newPayments);
            setPaymentSum(newPaymentSum);
            setHasLoaded(true);
        }
    }, [mounted]);

    const refreshPayments = useCallback(() => {
        (async function refresh() {
            if (loading || !paymentFormRef?.current?.values || !paymentDrsValid) return;
            if (mounted.current) setLoading(true);

            try {
                const { startDate, endDate, selectedCenter, filterType, filterQuery } = paymentFormRef.current.values;
                const centerValue = selectedCenter?.value === 'all' ? 'all' : selectedCenter?.value;
                const paymentsRes = await fetchPaymentsDaterangeCenter({
                    startDate,
                    endDate,
                    center: centerValue
                });
                const studentsRes = await fetchStudentsEssential();
                
                const isApiError = checkResponses(paymentsRes, studentsRes);
                if (isApiError) {
                    if (mounted.current) {
                        const authError = paymentsRes?.status === 401 || studentsRes?.status === 401;
                        if (authError) {
                            setApiError('Authentication error. Please refresh the page or log in again.');
                        } else {
                            setApiError('Error fetching payment data from the server. Please try again later.');
                        }
                        setLoading(false);
                        setHasLoaded(true);
                    }
                    return;
                } else setApiError(false);

                const newPayments = paymentsRes.data?.payments || [];
                const newContracts = paymentsRes.data?.contracts || [];
                const newStudents = studentsRes.data || [];

                const contractMap = {};
                newContracts.forEach(c => contractMap[parseInt(c.id)] = c);
                const studentToNameMap = {};
                newStudents.forEach(s => studentToNameMap[s.user_id] = `${s.first_name} ${s.last_name}`);
                
                const paymentsAppended = newPayments.map(p => {
                    p.contractDetails = contractMap[parseInt(p.contract)] || {};
                    p.studentName = studentToNameMap[p.contractDetails.student] || `Unknown student (UID: ${p.contractDetails.student})`;
                    return p;
                });

                if (mounted.current) {
                    setPayments(paymentsAppended);
                    filterPayments(paymentsAppended, filterType, filterQuery);
                    setLoading(false);
                }
            } catch (error) {
                if (mounted.current) {
                    setApiError('Error fetching payment data. Please try again later.');
                    setLoading(false);
                    setHasLoaded(true);
                }
            }
        })();
    }, [loading, paymentFormRef, paymentDrsValid, fetchPaymentsDaterangeCenter, fetchStudentsEssential, filterPayments, mounted]);


    const refreshData = useCallback(() => {
        (async function refresh() {
            if (loading || !formRef.current?.values) return;
            if (mounted.current) setLoading(true);

            try {
                const { startDate, endDate, selectedCenter } = formRef.current.values;
                const contractsRes = await fetchContractsDaterangeCenter({ 
                    startDate, 
                    endDate, 
                    center: selectedCenter?.value === 'all' ? null : selectedCenter?.value 
                });
                const centersRes = await fetchRpCentersAll();
                const studentsRes = await fetchStudentsAll();
                
                const isApiError = checkResponses(contractsRes, centersRes, studentsRes);
                if (isApiError) {
                    if (mounted.current) {
                        // Check if it's an authentication error
                        const authError = contractsRes?.status === 401 || centersRes?.status === 401 || studentsRes?.status === 401;
                        if (authError) {
                            setApiError('Authentication error. Please refresh the page or log in again.');
                        } else {
                            setApiError('Error fetching data from the server. Please try again later.');
                        }
                        setLoading(false);
                        setHasLoaded(true);
                    }
                    return;
                } else setApiError(false);

                // Backend returns { contracts: [], payments: [] }
                const contractsData = contractsRes.data || {};
                const newContracts = Array.isArray(contractsData.contracts) ? contractsData.contracts : getDataArray(contractsRes);
                const newPayments = Array.isArray(contractsData.payments) ? contractsData.payments : [];
                const newCenters = getDataArray(centersRes);
                const newStudents = getDataArray(studentsRes);

                const centerMap = {};
                newCenters.forEach(c => centerMap[parseInt(c.id)] = c.name);
                const studentMap = {};
                const studentCenterMap = {};
                const studentActiveMap = {};
                newStudents.forEach(s => {
                    studentMap[s.user_id] = `${s.first_name} ${s.last_name}`;
                    studentCenterMap[s.user_id] = parseInt(s.rp_primary_center);
                    studentActiveMap[s.user_id] = parseInt(s.rp_active);
                });

                // Calculate balances and payment status (ReadingPlex-specific)
                const contractsWithBalances = calculateRpContractBalances(newContracts, newPayments);
                
                const contractsAppended = contractsWithBalances.map(c => {
                    const studentCenter = studentCenterMap[c.student];
                    c.centerName = centerMap[studentCenter] || `Unknown center`;
                    c.studentName = studentMap[c.student] || `Unknown student`;
                    c.studentActive = studentActiveMap[c.student] || 0;
                    
                    // Calculate payment status from balances
                    const balancePaid = Number(c.balancePaid ?? 0);
                    const balanceOwed = Number(c.balanceOwed ?? 0);
                    const balanceDiff = balancePaid - balanceOwed;

                    let paymentStatusLabel = 'Unknown';
                    if (balanceOwed === 0 && balancePaid === 0) {
                        paymentStatusLabel = 'Unknown'; // No payments expected or made
                    } else if (balanceDiff === 0) {
                        paymentStatusLabel = 'Matching'; // Paid exactly what's owed
                    } else if (balanceDiff < 0) {
                        paymentStatusLabel = `Under (${Math.abs(balanceDiff)})`; // Paid less than owed
                    } else if (balanceDiff > 0) {
                        paymentStatusLabel = `Over (${Math.abs(balanceDiff)})`; // Paid more than owed
                    }
                    
                    c.payment_status = paymentStatusLabel;
                    return c;
                });

                const { filterQuery, activeContractsOnly, activeStudentsOnly, showExtra, hideMatching, monthlyOnly } = formRef.current.values;

                if (mounted.current) {
                    setContracts(contractsAppended);
                    setCenters(newCenters);
                    setStudents(newStudents);
                    filterContracts(contractsAppended, filterQuery, {
                        activeContractsOnly, activeStudentsOnly, showExtra, hideMatching, monthlyOnly
                    });
                    setHasLoaded(true);
                    setLoading(false);
                }
            } catch (error) {
                if (mounted.current) {
                    // Check if it's a network or authentication error
                    const errorMessage = error?.message || error?.toString() || 'Unknown error';
                    if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
                        setApiError('Authentication error. Please refresh the page or log in again.');
                    } else if (errorMessage.includes('Network') || errorMessage.includes('fetch')) {
                        setApiError('Network error. Please check your connection and try again.');
                    } else {
                        setApiError('Error fetching data. Please try again later.');
                    }
                    setLoading(false);
                    setHasLoaded(true);
                }
            }
        })();
    }, [mounted, loading, fetchContractsDaterangeCenter, fetchRpCentersAll, fetchStudentsAll, filterContracts]);

    useEffect(() => {
        refreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Initialize payments data when payments tab is first opened
    useEffect(() => {
        if (activeTab === 'payments' && centers.length > 0 && paymentFormRef.current?.values && paymentDrsValid) {
            // Centers are loaded and form is initialized, now we can fetch payments
            refreshPayments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, centers.length, paymentDrsValid]);

    // Export to CSV
    const handleExportCSV = () => {
        const csvData = filteredContracts.map(c => ({
            'Student': c.studentName || '',
            'Contract Type': c.type || '',
            'Contract Start': c.start_date || '',
            'Contract End': c.end_date || '',
            'Payment Per Cycle': c.payment_per_cycle || '',
            'Payment Status': c.payment_status || '',
            'Notes': c.notes || '',
            'Active': parseInt(c.active) === 1 ? 'Active' : 'Inactive'
        }));
        exportToCSV(csvData, 'Contracts_Export');
    };

    const handleShowPaymentDetails = (contract) => {
        setSelectedContractForPaymentDetails(contract);
        setShowPaymentDetailsModal(true);
    };

    const handleClosePaymentDetailsModal = () => {
        setShowPaymentDetailsModal(false);
        setSelectedContractForPaymentDetails(null);
    };

    // Columns per Guide Page 10: Student, Contract Type, Contract Start, Contract End, Payment Per Cycle, Payment Status, Notes, Active, Actions
    const getColumns = () => [
        { 
            dataField: 'studentName', 
            text: 'Student', 
            sort: true,
            formatter: (row) => (
                <span className="font-semibold">{row.studentName}</span>
            )
        },
        { 
            dataField: 'type', 
            text: 'Contract Type', 
            sort: true,
            formatter: (row) => (
                <Label color={row.type?.toLowerCase().includes('reading') ? 'mpLBlue' : 
                             row.type?.toLowerCase().includes('writing') ? 'mpGreen' : 'mpOrange'}>
                    {row.type || 'Unknown'}
                </Label>
            )
        },
        {
            dataField: 'start_date',
            text: 'Contract Start',
            sort: true,
            formatter: (row) => row.start_date ? formatDate(row.start_date) : '-'
        },
        {
            dataField: 'end_date',
            text: 'Contract End',
            sort: true,
            formatter: (row) => {
                if (!row.end_date) return '-';
                const endDateFormatted = formatDate(row.end_date);
                // Treat invalid dates as open contracts (no end date)
                if (endDateFormatted === 'Invalid date' || endDateFormatted === 'Invalid Date') {
                    return (
                        <Label color="mpYellow">
                            Never
                        </Label>
                    );
                }
                return endDateFormatted;
            }
        },
        {
            dataField: 'payment_per_cycle',
            text: 'Payment Per Cycle',
            sort: true,
            formatter: (row) => row.payment_per_cycle ? `$${row.payment_per_cycle}` : '-'
        },
        {
            dataField: 'payment_status',
            text: 'Payment Status',
            sort: true,
            formatter: (row) => {
                const status = row.payment_status || 'Unknown';
                const balancePaid = Number(row.balancePaid ?? 0);
                const balanceOwed = Number(row.balanceOwed ?? 0);
                const balanceDiff = balancePaid - balanceOwed;
                
                // Determine color based on payment status
                let color = 'mpLGrey';
                if (status.includes('Matching')) color = 'mpGreen';
                else if (status.includes('Under')) color = 'mpLRed';
                else if (status.includes('Over')) color = 'mpYellow';
                
                // Build monthly breakdown tooltip with FIFO allocation details
                const monthlyBreakdown = row.monthlyBreakdown || [];
                let tooltipContent = (
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                        <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #ddd' }}>
                            <div style={{ marginBottom: '4px' }}><strong>Total Expected:</strong> ${balanceOwed.toFixed(2)}</div>
                            <div style={{ marginBottom: '4px' }}><strong>Total Paid:</strong> ${balancePaid.toFixed(2)}</div>
                            <div style={{ marginBottom: '4px' }}>
                                <strong>Total {balanceDiff < 0 ? 'Underpaid' : balanceDiff > 0 ? 'Overpaid' : 'Balance'} (Since Contract Start):</strong> 
                                <span style={{ 
                                    color: balanceDiff >= 0 ? '#28a745' : '#dc3545',
                                    fontWeight: 'bold',
                                    marginLeft: '4px'
                                }}>
                                    {balanceDiff >= 0 ? '+' : ''}${Math.abs(balanceDiff).toFixed(2)}
                                </span>
                            </div>
                        </div>
                        {monthlyBreakdown.length > 0 && (
                            <>
                                <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>Monthly Breakdown (FIFO Allocation):</div>
                                <div style={{ 
                                    maxHeight: '300px', 
                                    overflowY: 'auto', 
                                    marginTop: '4px',
                                    fontSize: '12px'
                                }}>
                                    {monthlyBreakdown.map((month, idx) => (
                                        <div key={idx} style={{ 
                                            marginBottom: '8px', 
                                            padding: '4px',
                                            backgroundColor: idx % 2 === 0 ? '#f9f9f9' : 'transparent',
                                            borderRadius: '2px'
                                        }}>
                                            <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>{month.monthLabel}:</div>
                                            <div style={{ marginLeft: '8px', fontSize: '11px' }}>
                                                <div>Due: <strong>${month.due.toFixed(2)}</strong></div>
                                                <div>Paid: <strong>${month.paid.toFixed(2)}</strong></div>
                                                <div>
                                                    Balance: 
                                                    <span style={{ 
                                                        color: month.balance >= 0 ? '#28a745' : '#dc3545',
                                                        fontWeight: 'bold',
                                                        marginLeft: '4px'
                                                    }}>
                                                        {month.balance >= 0 ? '+' : ''}${month.balance.toFixed(2)}
                                                    </span>
                                                </div>
                                                {month.runningBalance !== undefined && (
                                                    <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                                                        Running Balance: ${month.runningBalance.toFixed(2)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                );
                
                return (
                    <button
                        onClick={() => handleShowPaymentDetails(row)}
                        className="cursor-pointer hover:opacity-80 transition-opacity"
                        style={{ background: 'none', border: 'none', padding: 0 }}
                    >
                        <Label color={color}>{status}</Label>
                    </button>
                );
            }
        },
        {
            dataField: 'notes',
            text: 'Notes',
            formatter: (row) => (
                <div className="text-sm max-w-xs truncate">
                    {row.notes || <span className="text-gray-600">-</span>}
                </div>
            )
        },
        {
            dataField: 'active',
            text: 'Active',
            sort: true,
            formatter: (row) => (
                <Label color={parseInt(row.active) === 1 ? 'mpGreen' : 'mpLRed'}>
                    {parseInt(row.active) === 1 ? 'Active' : 'Inactive'}
                </Label>
            )
        },
        {
            dataField: 'actions',
            text: 'Actions',
            formatter: (row) => (
                <div className="flex flex-row gap-x-2">
                    <TooltipWrapper tooltipText="Edit contract">
                        <Button
                            color="lte-mpLBlue"
                            variant="icon"
                                onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleOpenModal('edit', row);
                            }}
                        >
                            <SVGIcon fileName="pencil" />
                        </Button>
                    </TooltipWrapper>
                    <TooltipWrapper tooltipText="Delete contract">
                        <Button
                            color="lte-mpLRed"
                            variant="icon"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleOpenModal('delete', row);
                            }}
                        >
                            <SVGIcon fileName="trash" />
                        </Button>
                    </TooltipWrapper>
                </div>
            )
        }
    ];

    const defaultSorted = { dataField: 'studentName', order: 'asc' };

    const centerOptions = [{ value: 'all', label: 'All' }, ...centers.map(c => ({ value: parseInt(c.id), label: c.name }))];

    return (
        <div className="page-box">
            <BrowserTabTitle>{pageTitle}</BrowserTabTitle>
            {loading && <LoadingOverlay />}
            <div className="card">
                {/* Tabs per Guide Page 10 */}
                <div className="flex flex-row border-b mb-4">
                    <button
                        className={`px-4 py-2 font-semibold ${activeTab === 'contracts' ? 'text-mpLBlue border-b-2 border-mpLBlue' : 'text-gray-700'}`}
                        onClick={() => setActiveTab('contracts')}
                    >
                        CONTRACTS ({filteredContracts.length})
                    </button>
                    <button
                        className={`px-4 py-2 font-semibold ${activeTab === 'payments' ? 'text-mpLBlue border-b-2 border-mpLBlue' : 'text-gray-700'}`}
                        onClick={() => setActiveTab('payments')}
                    >
                        PAYMENTS ({filteredPayments.length})
                    </button>
                </div>
                
                <Formik
                    enableReinitialize
                    initialValues={{
                        startDate: startApi,
                        endDate: endApi,
                        selectedCenter: { value: 'all', label: 'All' },
                        filterQuery: '',
                        activeContractsOnly: true,
                        activeStudentsOnly: true,
                        showExtra: false,
                        hideMatching: false,
                        monthlyOnly: false
                    }}
                    innerRef={formRef}
                    onSubmit={() => null}
                >
                    {formik => (
                        <form onSubmit={formik.handleSubmit}>
                            {activeTab === 'contracts' && (
                                <>
                            <h2>Contracts ({filteredContracts.length})</h2>
                            <br/>
                            
                            {/* Date range and center selector */}
                            <div className="flex flex-row gap-x-4 items-end mb-4 flex-wrap">
                                <DateRangeSelector
                                    id="contracts-drs"
                                    startName="startDate"
                                    endName="endDate"
                                    startLabel="Start Date"
                                    endLabel="End Date"
                                    startValue={formik.values.startDate}
                                    endValue={formik.values.endDate}
                                    defaultValid={true}
                                    onStartChange={formik.handleChange}
                                    onEndChange={formik.handleChange}
                                />
                                <div className="w-32">
                                    <SelectSingle
                                        id="contracts-center"
                                        name="selectedCenter"
                                        value={formik.values.selectedCenter}
                                        options={centerOptions}
                                        onChange={formik.handleChange}
                                    />
                                </div>
                                <Button
                                    color="lte-mpLBlue"
                                    onClick={() => refreshData()}
                                >
                                    Search
                                </Button>
                            </div>
                            
                            {/* Filter row with toggles per Guide Page 10 */}
                            <div className="mb-4">
                                <h4>Filter Results ({filteredContracts.length})</h4>
                                <div className="flex flex-row gap-x-4 items-center flex-wrap">
                                    <div className="w-32">
                                        <SelectSingle
                                            id="contracts-filter-type"
                                            name="filterType"
                                            value={{ value: 'all', label: 'All' }}
                                            options={[{ value: 'all', label: 'All' }]}
                                            onChange={formik.handleChange}
                                        />
                                    </div>
                                    <div className="flex-grow">
                                    <FormikControl
                                        id="contracts-searchQuery"
                                        name="filterQuery"
                                            placeholder="Enter a filter query..."
                                        value={formik.values.filterQuery}
                                            onChange={(e) => {
                                                formik.handleChange(e);
                                                const filters = {
                                                    activeContractsOnly: formik.values.activeContractsOnly,
                                                    activeStudentsOnly: formik.values.activeStudentsOnly,
                                                    showExtra: formik.values.showExtra,
                                                    hideMatching: formik.values.hideMatching,
                                                    monthlyOnly: formik.values.monthlyOnly
                                                };
                                                filterContracts(contracts, e.target.value, filters);
                                            }}
                                        />
                                    </div>
                                    <Button
                                        color="lte-mpLBlue"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleOpenModal('create');
                                        }}
                                    >
                                        + New Contract
                                    </Button>
                                    <Button
                                        color="lte-mpTeal"
                                        onClick={handleExportCSV}
                                    >
                                        Export Contracts to CSV
                                    </Button>
                                </div>
                            </div>
                            
                            {/* Toggle switches per Guide Page 10 */}
                            <div className="flex flex-row gap-x-4 items-center mb-4 flex-wrap">
                                <div className="flex items-center gap-x-2">
                                    <Switch
                                        id="contracts-activeContractsOnly"
                                        name="activeContractsOnly"
                                        color="mpLBlue"
                                        checked={formik.values.activeContractsOnly}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            filterContracts(contracts, formik.values.filterQuery, {
                                                ...formik.values,
                                                activeContractsOnly: e.target.checked
                                            });
                                        }}
                                    />
                                    <label className="text-sm text-gray-800">Active Contracts Only</label>
                                </div>
                                <div className="flex items-center gap-x-2">
                                    <Switch
                                        id="contracts-activeStudentsOnly"
                                        name="activeStudentsOnly"
                                        color="mpLBlue"
                                        checked={formik.values.activeStudentsOnly}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            filterContracts(contracts, formik.values.filterQuery, {
                                                ...formik.values,
                                                activeStudentsOnly: e.target.checked
                                            });
                                        }}
                                    />
                                    <label className="text-sm text-gray-800">Active Students Only</label>
                                </div>
                                <div className="flex items-center gap-x-2">
                                    <Switch
                                        id="contracts-showExtra"
                                        name="showExtra"
                                        color="mpLBlue"
                                        checked={formik.values.showExtra}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            filterContracts(contracts, formik.values.filterQuery, {
                                                ...formik.values,
                                                showExtra: e.target.checked
                                            });
                                        }}
                                    />
                                    <label className="text-sm text-gray-800">Show Extra</label>
                                </div>
                                <div className="flex items-center gap-x-2">
                                    <Switch
                                        id="contracts-hideMatching"
                                        name="hideMatching"
                                        color="mpLBlue"
                                        checked={formik.values.hideMatching}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            filterContracts(contracts, formik.values.filterQuery, {
                                                ...formik.values,
                                                hideMatching: e.target.checked
                                            });
                                        }}
                                    />
                                    <label className="text-sm text-gray-800">Hide Matching</label>
                                </div>
                                <div className="flex items-center gap-x-2">
                                    <Switch
                                        id="contracts-monthlyOnly"
                                        name="monthlyOnly"
                                        color="mpLBlue"
                                        checked={formik.values.monthlyOnly}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            filterContracts(contracts, formik.values.filterQuery, {
                                                ...formik.values,
                                                monthlyOnly: e.target.checked
                                            });
                                        }}
                                    />
                                    <label className="text-sm text-gray-800">Monthly Only</label>
                                </div>
                            </div>
                            
                            {apiError ? <div className="text-mpLRed">{apiError}</div> :
                                hasLoaded && activeTab === 'contracts' &&
                                <Table
                                    tableName="contracts"
                                    data={filteredContracts}
                                    columns={getColumns()}
                                    defaultSorted={defaultSorted}
                                    emptyMessage="No contracts found"
                                />
                            }
                                </>
                            )}
                            {activeTab === 'payments' && (
                                <div>
                                    <h2>Payments ({payments.length})</h2>
                                    <br/>
                                    <Formik
                                        enableReinitialize
                                        initialValues={{
                                            startDate: startApi,
                                            endDate: endApi,
                                            selectedCenter: { value: 'all', label: 'All' },
                                            filterType: { value: 'all', label: 'All' },
                                            filterQuery: '',
                                        }}
                                        innerRef={paymentFormRef}
                                        onSubmit={refreshPayments}
                                    >
                                        {paymentFormik => (
                                            <form onSubmit={paymentFormik.handleSubmit}>
                                                <div className="flex flex-row gap-x-4 items-end mb-4 flex-wrap">
                                                    <DateRangeSelector
                                                        id="payments-drs"
                                                        startName="startDate"
                                                        endName="endDate"
                                                        startLabel="Start Date"
                                                        endLabel="End Date"
                                                        startValue={paymentFormik.values.startDate}
                                                        endValue={paymentFormik.values.endDate}
                                                        defaultValid={true}
                                                        onStartChange={paymentFormik.handleChange}
                                                        onEndChange={paymentFormik.handleChange}
                                                        onChangeValidation={setPaymentDrsValid}
                                                    />
                                                    <div className="w-32">
                                                        <SelectSingle
                                                            id="payments-center"
                                                            name="selectedCenter"
                                                            value={paymentFormik.values.selectedCenter}
                                                            options={centerOptions}
                                                            onChange={paymentFormik.handleChange}
                                                        />
                                                    </div>
                                                    <Button
                                                        color="lte-mpLBlue"
                                                        onClick={() => refreshPayments()}
                                                    >
                                                        Search
                                                    </Button>
                                                </div>
                                                
                                                <div className="mb-4">
                                                    <h4>Filter Results ({filteredPayments.length} payments, ${paymentSum})</h4>
                                                    <div className="flex flex-row gap-x-4 items-center flex-wrap mt-2">
                                                        <div className="w-32">
                                                            <SelectSingle
                                                                id="payments-filter-type"
                                                                name="filterType"
                                                                value={paymentFormik.values.filterType}
                                                                options={[
                                                                    { value: 'all', label: 'All' },
                                                                    { value: 'studentName', label: 'Student Name' },
                                                                    { value: 'notes', label: 'Notes' }
                                                                ]}
                                                                onChange={(e) => {
                                                                    paymentFormik.handleChange(e);
                                                                    filterPayments(payments, paymentFormik.values.filterType, paymentFormik.values.filterQuery || '');
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="flex-grow">
                                                            <FormikControl
                                                                id="payments-searchQuery"
                                                                name="filterQuery"
                                                                placeholder="Enter a filter query..."
                                                                value={paymentFormik.values.filterQuery}
                                                                onChange={(e) => {
                                                                    paymentFormik.handleChange(e);
                                                                    filterPayments(payments, paymentFormik.values.filterType, e.target.value);
                                                                }}
                                                            />
                                                        </div>
                                                        <Button
                                                            color="lte-mpLBlue"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                setPaymentModalMode('create');
                                                                setSelectedPayment(null);
                                                                setShowPaymentModal(true);
                                                            }}
                                                        >
                                                            + New Payment
                                                        </Button>
                                                    </div>
                                                </div>
                                            </form>
                                        )}
                                    </Formik>
                                    
                                    {hasLoaded && 
                                        <PaymentsTable
                                            tableName="payments"
                                            payments={filteredPayments}
                                            refreshData={refreshPayments}
                                        />
                                    }
                                </div>
                            )}
                        </form>
                    )}
                </Formik>
            </div>
            <Socket refreshData={refreshData} page={pageTitle} setVersion={props.setVersion} />
            
            {showModal && (
                isReadingPlex ? (
                    <ContractModal
                        key={`contract-modal-${modalMode}-${selectedContract?.id || 'new'}-${Date.now()}`}
                        show={showModal}
                        mode={modalMode}
                        contract={selectedContract}
                        students={students}
                        centers={centers}
                        onClose={handleCloseModal}
                        createContract={createContract}
                        updateContract={updateContract}
                        deleteContract={deleteContract}
                    />
                ) : (
                    <MathPlexContractModal
                        key={`contract-modal-${modalMode}-${selectedContract?.id || 'new'}-${Date.now()}`}
                        show={showModal}
                        mode={modalMode}
                        contract={selectedContract}
                        students={students}
                        centers={centers}
                        onClose={handleCloseModal}
                        createContract={createContract}
                        updateContract={updateContract}
                        deleteContract={deleteContract}
                    />
                )
            )}
            
            {showPaymentModal && (
                isReadingPlex ? (
                    <ReadingPlexPaymentsModal
                        key={`payment-modal-${paymentModalMode}-${selectedPayment?.id || 'new'}`}
                        show={showPaymentModal}
                        mode={paymentModalMode}
                        payment={selectedPayment}
                        students={students}
                        onClose={(shouldRefresh) => {
                            setShowPaymentModal(false);
                            setSelectedPayment(null);
                            if (shouldRefresh) {
                                setTimeout(() => {
                                    refreshPayments();
                                    refreshData(); // Also refresh contracts to update payment status
                                }, 500);
                            }
                        }}
                        createPayment={createPayment}
                        updatePayment={updatePayment}
                        deletePayment={deletePayment}
                    />
                ) : (
                    <MathPlexPaymentsModal
                        key={`payment-modal-${paymentModalMode}-${selectedPayment?.id || 'new'}`}
                        show={showPaymentModal}
                        mode={paymentModalMode}
                        payment={selectedPayment}
                        students={students}
                        onClose={(shouldRefresh) => {
                            setShowPaymentModal(false);
                            setSelectedPayment(null);
                            if (shouldRefresh) {
                                setTimeout(() => {
                                    refreshPayments();
                                    refreshData(); // Also refresh contracts to update payment status
                                }, 500);
                            }
                        }}
                        createPayment={createPayment}
                        updatePayment={updatePayment}
                        deletePayment={deletePayment}
                    />
                )
            )}

            {/* Payment Details Modal */}
            {showPaymentDetailsModal && selectedContractForPaymentDetails && (
                <Modal show={showPaymentDetailsModal} onHide={handleClosePaymentDetailsModal}>
                    <Modal.Header>
                        <h2 className="text-xl font-semibold">Payment Details - {selectedContractForPaymentDetails.studentName}</h2>
                    </Modal.Header>
                    <Modal.Body>
                        {(() => {
                            const row = selectedContractForPaymentDetails;
                            const balancePaid = Number(row.balancePaid ?? 0);
                            const balanceOwed = Number(row.balanceOwed ?? 0);
                            const balanceDiff = balancePaid - balanceOwed;
                            const monthlyBreakdown = row.monthlyBreakdown || [];
                            let index = 0;
                            
                            return (
                                <div style={{ fontSize: '14px', lineHeight: '1.8' }}>
                                    <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '2px solid #ddd' }}>
                                        <div style={{ marginBottom: '8px', fontSize: '16px', fontWeight: 'bold' }}>Summary</div>
                                        <div style={{ marginBottom: '6px' }}><strong>Total Expected:</strong> ${balanceOwed.toFixed(2)}</div>
                                        <div style={{ marginBottom: '6px' }}><strong>Total Paid:</strong> ${balancePaid.toFixed(2)}</div>
                                        <div style={{ marginBottom: '6px' }}>
                                            <strong>Total {balanceDiff < 0 ? 'Underpaid' : balanceDiff > 0 ? 'Overpaid' : 'Balance'} (Since Contract Start):</strong> 
                                            <span style={{ 
                                                color: balanceDiff >= 0 ? '#28a745' : '#dc3545',
                                                fontWeight: 'bold',
                                                marginLeft: '8px',
                                                fontSize: '16px'
                                            }}>
                                                {balanceDiff >= 0 ? '+' : ''}${Math.abs(balanceDiff).toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                    {monthlyBreakdown.length > 0 && (
                                        <>
                                            <div style={{ marginBottom: '12px', fontWeight: 'bold', fontSize: '16px' }}>Monthly Breakdown (FIFO Allocation):</div>
                                            <div style={{ 
                                                maxHeight: '400px', 
                                                overflowY: 'auto', 
                                                marginTop: '8px',
                                                fontSize: '13px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                padding: '8px'
                                            }}>
                                                {monthlyBreakdown.map((month, idx) => (
                                                    <div key={idx} style={{ 
                                                        marginBottom: '12px', 
                                                        padding: '8px',
                                                        backgroundColor: idx % 2 === 0 ? '#f9f9f9' : 'transparent',
                                                        borderRadius: '4px'
                                                    }}>
                                                        <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '14px' }}>{month.monthLabel}:</div>
                                                        <div style={{ marginLeft: '12px', fontSize: '12px' }}>
                                                            <div>Due: <strong>${month.due.toFixed(2)}</strong></div>
                                                            <div>Paid: <strong>${month.paid.toFixed(2)}</strong></div>
                                                            <div>
                                                                Balance: 
                                                                <span style={{ 
                                                                    color: month.balance >= 0 ? '#28a745' : '#dc3545',
                                                                    fontWeight: 'bold',
                                                                    marginLeft: '4px'
                                                                }}>
                                                                    {month.balance >= 0 ? '+' : ''}${month.balance.toFixed(2)}
                                                                </span>
                                                            </div>
                                                            {month.runningBalance !== undefined && (
                                                                <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                                                                    Running Balance: ${month.runningBalance.toFixed(2)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                    {monthlyBreakdown.length === 0 && row.relevantPayments?.length !== 0 && (
                                        <>
                                            <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid #ccc' }} />
                                            <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '16px' }}>Payment History:</div>
                                            <div style={{ 
                                                maxHeight: '300px', 
                                                overflowY: 'auto',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                padding: '8px'
                                            }}>
                                                {row.relevantPayments.map(rp => {
                                                    return (
                                                        <div key={`c-${row.id}-p-${rp.id}-${index++}`} style={{ marginBottom: '4px' }}>
                                                            {formatDate(rp.date)}: ${rp.amount}
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </>
                                    )}
                                    {monthlyBreakdown.length === 0 && (!row.relevantPayments || row.relevantPayments.length === 0) && (
                                        <div style={{ color: '#666', fontStyle: 'italic' }}>No payment history available.</div>
                                    )}
                                </div>
                            );
                        })()}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button color="mpLBlue" onClick={handleClosePaymentDetailsModal}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
};

export default connect(null, { 
    fetchContractsDaterangeCenter,
    fetchContractsIds,
    fetchRpCentersAll, 
    fetchStudentsAll,
    createContract,
    updateContract,
    deleteContract,
    fetchPaymentsDaterangeCenter,
    fetchPaymentsIds,
    fetchStudentsEssential,
    fetchContractsStudentNames,
    createPayment,
    updatePayment,
    deletePayment
})(Contracts);
